import React, { useRef, useState } from "react";
import "../sda/styles.scss";
import { Modal, ModalContent } from "@amzn/stencil-react-components/modal";
import {
  Button,
  ButtonSize,
  ButtonVariant,
} from "@amzn/stencil-react-components/button";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Label } from "../typography";
import { Text } from "@amzn/stencil-react-components/text";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { withdrawApplication } from "../../utility/application-data";
import { Radio } from "@amzn/stencil-react-components/form";
import useArb from "../../hooks/use-arb";
import { PopoverController } from "@amzn/stencil-react-components/popover";
import { popoverTextColor } from "../../config/palette";
import { IconMoreHorizontal } from "@amzn/stencil-react-components/icons";
import "./styles.scss";
import { useNavigatorWithURLReady } from "../../hooks/use-navigator";
import { DisplayNewUIComponent } from "../../utility/new-ui-toggle";
import { isCustomerService } from "../../utility/app-config-helper";

interface ManageApplicationCardModalProps {
  onChangeScheduleClick: () => void;
  onGoToDashboardClick: () => void;
  onWithdrawApplication: () => void;
  onWithdrawApplicationConfirm: (withdrawReason: string) => void;
  applicationId: string;
  eligibleForCSS: boolean;
  eligibleForWithdraw: boolean;
  eligibleForAppointmentRescheduling: boolean;
  appointmentSelfServiceURL: string;
}

export const ManageApplicationCardModal = ({
  onChangeScheduleClick,
  onGoToDashboardClick,
  onWithdrawApplication,
  onWithdrawApplicationConfirm,
  applicationId,
  eligibleForCSS,
  eligibleForWithdraw,
  eligibleForAppointmentRescheduling,
  appointmentSelfServiceURL,
}: ManageApplicationCardModalProps) => {
  const bundle = useArb("HVHChecklistPantherResource");

  // For popover button
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const target = useRef(null);
  const togglePopover = () => setIsPopoverOpen(!isPopoverOpen);
  const closePopover = () => setIsPopoverOpen(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hideWarningMessage, setHideWarningMessage] = useState(true);
  const [isWithdrawConfirmationOpen, setIsWithdrawConfirmationOpen] = useState(
    false
  );
  const [isLoadingScreenOpen, setIsLoadingScreenOpen] = useState(false);
  const [
    isProgressCompletedViewOpen,
    setIsProgressCompletedViewOpen,
  ] = useState(false);
  const [isProgressFailedViewOpen, setIsProgressFailedViewOpen] = useState(
    false
  );
  const [withdrawReason, setWithdrawReason] = useState<number>();

  const openModal = () => {
    setIsModalOpen(true);
    setIsWithdrawConfirmationOpen(true);
    onWithdrawApplication();
  };

  const closeModal = () => {
    closeAllContents();
    setIsModalOpen(false);
    setWithdrawReason(undefined);
    setHideWarningMessage(true);
  };

  const closeAllContents = () => {
    setIsWithdrawConfirmationOpen(false);
    setIsLoadingScreenOpen(false);
    setIsProgressCompletedViewOpen(false);
    setIsProgressFailedViewOpen(false);
  };

  const displayLoadingScreen = () => {
    closeAllContents();
    setIsLoadingScreenOpen(true);
  };

  const displayProgressCompletedView = () => {
    closeAllContents();
    setIsProgressCompletedViewOpen(true);
  };

  const displayProgressFailedView = () => {
    closeAllContents();
    setIsProgressFailedViewOpen(true);
  };

  const onClickWithdrawApplication = async () => {
    if (withdrawReason === undefined) {
      setHideWarningMessage(false);
    } else {
      displayLoadingScreen();
      try {
        const response = await withdrawApplication(
          applicationId,
          payloadEnReasonCodeForMx[withdrawReason]
        );
        if (response.statusCode === 200) {
          onWithdrawApplicationConfirm(reasonCode[withdrawReason]);
          displayProgressCompletedView();
        } else {
          displayProgressFailedView();
        }
      } catch (err) {
        displayProgressFailedView();
      }
    }
  };

  const onChangeWithdrawReason = (selection: any) => {
    setWithdrawReason(selection.target.value);
    setHideWarningMessage(true);
  };

  const payloadEnReasonCodeForMx = [
    "Accepted another offer",
    "Not interested in job location",
    "Shift doesn't work for me",
    "Pay rate doesn't meet expectation",
    "None of the above reasons",
  ];

  const reasonCode = [
    bundle.getMessage("Checklist-CardModal-ReasonCode1"),
    bundle.getMessage("Checklist-CardModal-ReasonCode2"),
    bundle.getMessage("Checklist-CardModal-ReasonCode3"),
    bundle.getMessage("Checklist-CardModal-ReasonCode4"),
    bundle.getMessage("Checklist-CardModal-ReasonCode5"),
  ];

  const reasonContent = (
    <>
      <Col gridGap="1rem">
        {reasonCode.map((item, i) => {
          return (
            <Row gridGap="S200" alignItems="center" key={i}>
              <Radio
                id={i.toString()}
                name={"radio"}
                value={i.toString()}
                onChange={onChangeWithdrawReason}
              />
              <Label htmlFor={i.toString()}>{item}</Label>
            </Row>
          );
        })}
      </Col>
    </>
  );

  const withdrawApplicationConfirmation = (
    <ModalContent
      titleText={bundle.getMessage("Checklist-CardModal-Withdraw")}
      buttons={[
        <>
          <Col width="100%">
            <Row width="100%" gridGap="1rem">
              <Col width="100%">
                <Button onClick={closeModal} size={ButtonSize.Small}>
                  {bundle.getMessage("Checklist-CardModal-Cancel")}
                </Button>
              </Col>
              <Col width="100%">
                <Button
                  onClick={onClickWithdrawApplication}
                  variant={ButtonVariant.Primary}
                  size={ButtonSize.Small}
                >
                  {bundle.getMessage("Checklist-CardModal-Confirm")}
                </Button>
              </Col>
            </Row>
          </Col>
        </>,
      ]}
    >
      <Col gridGap="1rem">
        <Col gridGap="0.5rem">
          <Text fontSize="T300">
            {bundle.getMessage("Checklist-CardModal-WithdrawQuestion")}
          </Text>
          <Text fontSize="T200">
            {bundle.getMessage("Checklist-CardModal-TellReason")}
          </Text>
        </Col>
        {reasonContent}
        <Text fontSize="T200" color="red" hidden={hideWarningMessage}>
          {bundle.getMessage("Checklist-CardModal-SelectReason")}
        </Text>
      </Col>
    </ModalContent>
  );

  const loadingScreen = (
    <ModalContent
      titleText={bundle.getMessage("Checklist-CardModal-Processing")}
    >
      <Col padding="1rem" alignItems="center">
        <Spinner size={SpinnerSize.Small} showText={false} />
      </Col>
    </ModalContent>
  );

  const progressCompletedView = (
    <ModalContent titleText={bundle.getMessage("Checklist-CardModal-Success")}>
      <Col alignItems="center">
        <Button variant={ButtonVariant.Primary} onClick={onGoToDashboardClick}>
          {bundle.getMessage("Checklist-CardModal-Dashboard")}
        </Button>
      </Col>
    </ModalContent>
  );

  const progressFailedView = (
    <ModalContent titleText={bundle.getMessage("Checklist-CardModal-Error")}>
      <Col alignItems="center">
        <Button variant={ButtonVariant.Primary} onClick={closeModal}>
          {bundle.getMessage("Checklist-General-CloseButtonText")}
        </Button>
      </Col>
    </ModalContent>
  );

  const popoverController = (
    <PopoverController
      target={target}
      isOpen={isPopoverOpen}
      close={closePopover}
    >
      <Col padding="1rem" gridGap="1rem" alignItems="center">
        {eligibleForWithdraw && (
          <Text
            className="text-button"
            color={popoverTextColor}
            onClick={openModal}
          >
            {bundle.getMessage("Checklist-CardModal-Withdraw")}
          </Text>
        )}
        {DisplayNewUIComponent && eligibleForAppointmentRescheduling && (
          <Text
            className="text-button"
            color={popoverTextColor}
            onClick={useNavigatorWithURLReady(appointmentSelfServiceURL)}
          >
            {bundle.getMessage(
              "Checklist-CardModal-AppointmentCard-Reschedule"
            )}
          </Text>
        )}
        {eligibleForCSS && (
          <Text
            className="text-button"
            color={popoverTextColor}
            onClick={onChangeScheduleClick}
          >
            {bundle.getMessage("Checklist-JobCard-ChangeSchedule")}
          </Text>
        )}
      </Col>
    </PopoverController>
  );

  return (
    <>
      <Modal
        isOpen={isModalOpen}
        close={closeModal}
        shouldCloseOnClickOutside={false}
      >
        {isModalOpen &&
          isWithdrawConfirmationOpen &&
          withdrawApplicationConfirmation}
        {isLoadingScreenOpen && loadingScreen}
        {isProgressFailedViewOpen && progressFailedView}
        {isProgressCompletedViewOpen && progressCompletedView}
      </Modal>

      <Col padding="1rem 1rem 1rem 0">
        {!isCustomerService(applicationId) && (
          <Button
            icon={<IconMoreHorizontal />}
            onClick={togglePopover}
            ref={target}
            size={ButtonSize.Small}
          />
        )}
        {popoverController}
      </Col>
    </>
  );
};
